import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { ENDPOINTS } from '../../helpers/constants';
import { makeRESTApiRequest } from '../../helpers/api';
import { blobToBase64, getBlobFromBlobURL } from '../../helpers/global';

export default function ShareQualia({
  show,
  onClose,
  pdfUrl,
  reportId,
  folderId,
  shareEndpoint = ENDPOINTS.SHARE_REPORT,
  onHistoryReportChange,
  onShareComplete,
  isPreliminaryReportStatus = false,
  isGeneratedReportWatermark = false
}) {
  const [submitting, setSubmitting] = useState(false);

  const savePdf = async () => {
    try {
      const blob = await getBlobFromBlobURL(pdfUrl);
      const base64Data = await blobToBase64(blob);

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.BASE64_FILE,
        method: 'POST',
        requestBody: {
          fileName: `report_${Date.now()}.pdf`,
          mimeType: 'application/pdf',
          data: base64Data.split(',')[1],
          ...(isPreliminaryReportStatus
            ? {
                isReportWatermark: isGeneratedReportWatermark === true,
                applyWaterMark: isGeneratedReportWatermark !== true
              }
            : {})
        }
      });

      if (error) {
        console.error('Error saving PDF:', error);
        return null;
      }
      return response;
    } catch (err) {
      console.error('Error saving PDF:', err);
      return null;
    }
  };

  const onShareClick = async () => {
    if (!reportId || !folderId) {
      toast.error('Missing report or folder information');
      return;
    }

    if (!pdfUrl) {
      toast.error('Report PDF is not available');
      return;
    }

    const sharingToastId = toast.loading('Saving report...');

    try {
      setSubmitting(true);
      onClose();

      const uploadedFileId = await savePdf();
      if (!uploadedFileId) {
        toast.dismiss(sharingToastId);
        toast.error('Failed to save PDF');
        setSubmitting(false);
        return;
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: shareEndpoint,
        method: 'POST',
        requestBody: {
          recepients: [],
          notes: '',
          cc: null,
          isPreliminaryReport: isPreliminaryReportStatus,
          skipEmail: true,
          fileId: uploadedFileId,
          reportId,
          folderId
        }
      });

      if (error) {
        toast.dismiss(sharingToastId);
        toast.error(error);
        setSubmitting(false);
        return;
      }

      onHistoryReportChange(response?.generatedFiles);
      onShareComplete?.();
      toast.dismiss(sharingToastId);
      toast.success('Report saved successfully');
      setSubmitting(false);
    } catch (err) {
      toast.dismiss(sharingToastId);
      toast.error('Failed to save report');
      setSubmitting(false);
      console.error('Failed to save report:', err);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="md" style={{ zIndex: 99999 }}>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="font-weight-bold xlargeFont">Sent Through Qualia</Modal.Title>
        <Button
          variant="link"
          onClick={onClose}
          disabled={submitting}
          className="p-0 mr-2 text-dark border-0 shadow-none"
        >
          <X size={20} />
        </Button>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="text-center mb-3">
          <img src="/qualia-icon.png" alt="Qualia" width={56} height={56} />
        </div>

        <p className="text-muted small text-center mb-0">
          {isPreliminaryReportStatus
            ? 'This will save the preliminary report to Google Drive. The order stays in Preliminary Report and is not moved to Pending To Pay. No email will be sent.'
            : 'This will save the final report to Google Drive and move the order to Pending To Pay. No email will be sent.'}
        </p>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="light" onClick={onClose} disabled={submitting} className="font-weight-bold small rounded">
          Cancel
        </Button>

        <Button variant="dark" onClick={onShareClick} disabled={submitting} className="font-weight-bold small rounded">
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
